import { FormControlLabel, Icon, Skeleton, Stack, Switch, Typography } from '@mui/material';
import { Channel, ChannelRole, Thread, ThreadStatus, ThreadUser } from '@schooly/api';
import { AvatarAuthMultiple } from '@schooly/components/avatar-auth';
import {
  ClosedThreadIcon,
  EmptyListSvg,
  EmptySchoolSvg,
  SimpleCard,
  ThreadIcon,
  TruncatedText,
} from '@schooly/style';
import { Counter } from '@schooly/style';
import { getUserFullName } from '@schooly/utils/user-helpers';
import { FC, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DefaultGenerics, FormatMessageResponse } from 'stream-chat';

import { useThreads } from './ThreadContext';
import { useChannelReadState } from './useChannelReadState';
import { formatChannelDate } from './utils';

type ThreadCardActionsProps = {
  unreadCount?: number;
  messageCount?: number;
  isMessageRead?: boolean;
  lastMessage?: {
    date: string | null;
    isRead: boolean;
  };
  isThreadClosed: boolean;
};

const ThreadCardActions: FC<ThreadCardActionsProps> = ({
  unreadCount,
  messageCount,
  lastMessage,
  isThreadClosed,
}) => {
  const count = unreadCount ? (
    <Counter sx={{ minWidth: 20, textAlign: 'center', fontWeight: 600, bgcolor: 'success.main' }}>
      {unreadCount}
    </Counter>
  ) : (
    <Stack direction="row" gap={0.25}>
      <Icon inverse sx={{ color: 'common.grey' }}>
        {isThreadClosed ? <ClosedThreadIcon /> : <ThreadIcon />}
      </Icon>
      <Typography color="common.grey">{messageCount}</Typography>
    </Stack>
  );

  return (
    <Stack alignItems="flex-end" gap={0.25} pt={0.75}>
      <Stack direction="row" height={12}>
        {lastMessage ? (
          <Stack direction="row" alignItems="center" gap={0.25}>
            <Typography className="last-message-date" variant="caption">
              {lastMessage.date}
            </Typography>
          </Stack>
        ) : null}
      </Stack>
      <Stack direction="row" gap={1} height={20}>
        {!!messageCount ? count : null}
      </Stack>
    </Stack>
  );
};

type ThreadSkeletonRowsProps = {
  rowCount: number;
};

export const ThreadSkeletonRows: FC<ThreadSkeletonRowsProps> = ({ rowCount }) => {
  return (
    <Stack>
      {[...new Array(rowCount)].map((_, i) => {
        return (
          <SimpleCard
            key={i}
            sx={{ width: '100%' }}
            loadingState={{
              avatarSize: 58,
              skeletons: [
                // biome-ignore lint/correctness/useJsxKeyInIterable: Legacy code
                <Skeleton variant="rectangular" width={94} height={12} />,
                // biome-ignore lint/correctness/useJsxKeyInIterable: Legacy code
                <Skeleton variant="rectangular" width={160} height={12} />,
              ],
            }}
          />
        );
      })}
    </Stack>
  );
};

type ThreadItemItemProps = {
  thread: Thread;
  onClick?: () => void;
  userId: string;
};

export const ThreadItem: FC<ThreadItemItemProps> = ({ thread, onClick, userId }) => {
  const { client, selectedThread } = useThreads();

  if (!client) throw new Error('Client was not provided');

  if (!thread.channel) {
    return (
      <ThreadCard
        thread={thread}
        selected={thread.id === selectedThread?.id}
        onClick={onClick}
        isThreadClosed={thread.status === ThreadStatus.ARCHIVED}
      />
    );
  }

  return (
    <ThreadCardWithChannel
      thread={thread}
      onClick={onClick}
      selected={thread.id === selectedThread?.id}
      threadChannel={thread.channel}
      userId={userId}
    />
  );
};

export type ChannelReadState = {
  lastReadMessageId: string | null;
  unreadCount: number;
  messageCount: number;
  lastMessage: FormatMessageResponse<DefaultGenerics> | null;
};

type ThreadCardWithChannelProps = {
  thread: Thread;
  threadChannel: Channel;
  onClick?: () => void;
  selected?: boolean;
  userId: string;
};

const ThreadCardWithChannel: FC<ThreadCardWithChannelProps> = ({
  thread,
  threadChannel,
  onClick,
  selected,
  userId,
}) => {
  const { streamChannel } = useThreads();

  const { readState } = useChannelReadState({
    selected: !!selected,
    canSubscribe: threadChannel.members.some(
      (m) => m.user.user_id === userId && m.role !== ChannelRole.CHANNEL_SPECTATOR,
    ),
    currentChannel: threadChannel,
    thread,
    userId,
    streamChannel,
  });

  const isThreadClosed =
    selected && !!streamChannel?.data
      ? !!streamChannel.data.frozen
      : thread.status === ThreadStatus.ARCHIVED;

  return (
    <ThreadCard
      onClick={onClick}
      thread={thread}
      selected={selected}
      unreadCount={readState.unreadCount}
      messageCount={readState.messageCount}
      isThreadClosed={isThreadClosed}
      lastMessage={
        readState.lastMessage
          ? {
              date: readState.lastMessage.created_at
                ? formatChannelDate(new Date(readState.lastMessage.created_at))
                : null,
              isRead: readState.lastMessage.status === 'received',
            }
          : undefined
      }
    />
  );
};

type ThreadCardProps = {
  thread: Thread;
  onClick?: () => void;
  selected?: boolean;
} & ThreadCardActionsProps;

const ThreadCard: FC<ThreadCardProps> = ({
  thread,
  unreadCount,
  messageCount,
  lastMessage,
  selected,
  onClick,
  isThreadClosed,
}) => {
  const [sortedChildren, sortedParents] = useGetSortedThreadParticipants(thread);

  const { formatMessage } = useIntl();

  const parentFullNames = useMemo(
    () => sortedParents.map((p) => getUserFullName(p)),
    [sortedParents],
  );

  return (
    <SimpleCard
      onClick={onClick}
      avatar={<AvatarAuthMultiple users={thread.children} />}
      actions={
        <ThreadCardActions
          isThreadClosed={isThreadClosed}
          unreadCount={unreadCount}
          messageCount={messageCount}
          lastMessage={lastMessage}
        />
      }
      sx={{
        '.card-content': {
          alignItems: 'flex-start',
        },
        '.MuiCardActionArea-focusHighlight': {
          background: 'transparent',
        },
        '.title ': {
          color: selected ? 'primary.main' : 'common.grey2',
        },
        '.sub-title, .last-message-date': {
          color: selected ? 'common.grey2' : 'common.grey',
        },
        backgroundColor: selected ? 'common.lightBg' : 'inherit',
        '&:hover': {
          backgroundColor: selected ? 'common.light2' : 'inherit',
          '.title': {
            color: 'primary.main',
          },
          '.sub-title, .last-message-date': {
            color: 'common.grey2',
          },
        },
      }}
    >
      <Stack width={140}>
        <TruncatedText
          items={sortedChildren.map((c) => getUserFullName(c))}
          maxLines={1}
          typographyProps={{
            className: 'title',
            variant: 'h3',
          }}
        />
        <TruncatedText
          items={parentFullNames.map((n, i) =>
            i === 0 ? `${formatMessage({ id: 'userType-parent-plural' })}: ${n}` : n,
          )}
          maxLines={2}
          tooltipProps={{ title: parentFullNames.join(', ') }}
          typographyProps={{ className: 'sub-title' }}
        />
      </Stack>
    </SimpleCard>
  );
};

type ThreadsUnreadProps = {
  onToggleUnread?: () => void;
  showUnread?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
};

export const ThreadsUnread: FC<ThreadsUnreadProps> = ({
  onToggleUnread,
  showUnread,
  isLoading,
  disabled,
}) => {
  return (
    <Stack alignItems="flex-end" py={1}>
      {isLoading ? (
        <Skeleton variant="rectangular" width={94} height={16} />
      ) : (
        <FormControlLabel
          control={<Switch checked={showUnread} onChange={onToggleUnread} />}
          label={<FormattedMessage id="messages-ThreadsUnreads" />}
          disabled={disabled}
          sx={{
            m: 0,
            color: showUnread ? 'text.primary' : 'text.secondary',
          }}
        />
      )}
    </Stack>
  );
};

type EmptyThreadsStubProps = {
  unread?: boolean;
};

export const EmptyThreadsStub: FC<EmptyThreadsStubProps> = ({ unread }) => {
  const { formatMessage } = useIntl();

  return (
    <Stack pt={8} alignItems="center">
      <Stack
        gap={2.5}
        sx={{
          ' svg': {
            width: 250,
            height: 250,
          },
        }}
      >
        {unread ? <EmptyListSvg /> : <EmptySchoolSvg />}
        <Typography textAlign="center" variant="h3" color="common.grey2" maxWidth={250}>
          {formatMessage({ id: unread ? 'messages-NoUnreadMessages' : 'messages-EmptyThreads' })}
        </Typography>
      </Stack>
    </Stack>
  );
};

export const useGetSortedThreadParticipants = (thread: Thread) =>
  useMemo(() => {
    const primaryContactIds = new Set(
      thread.children.map((child) => child.primary_contact_id).filter(Boolean),
    );
    const primaryParents: ThreadUser[] = [];
    const parents: ThreadUser[] = [];

    for (const parent of thread.parents) {
      if (!primaryContactIds.has(parent.user_id)) parents.push(parent);
      else primaryParents.push(parent);
    }

    const sortByName = <T extends { given_name: string }>(a: T, b: T) =>
      a.given_name.localeCompare(b.given_name);

    const sortedChildren = thread.children.sort(sortByName);
    const sortedParents = [...primaryParents.sort(sortByName), ...parents.sort(sortByName)];

    return [sortedChildren, sortedParents];
  }, [thread.parents, thread.children]);
